import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Grid, ListItemText, Button, Tab, Typography } from '@material-ui/core';
import { LogIn, LogOut } from 'react-feather';

import { TabPanel, TabList, TabContext } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';

import { ErrorComponent, LoadingSpinner, SectionCard, JwlTable, Chip } from '../../../../components';
import EditIcon from '@material-ui/icons/Edit';

import { StyledDivider } from './styles';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import {
  GQLDeliveryUnitSaleCatalogWithPlatformProducerContract,
  GQLParty
} from '@jewelutions/core-crm-server-api-client-domain';
import { Exception } from '@jewelutions/core-common-domain';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';
import PriceLabel from '../../../../components/PriceLabel';
import { CommonLogic } from '@jewelutions/core-common-logic';
import { OperationsHelper } from '../../../../operations/OperationHelper';
import { saveCsvFile } from '../../../../core/utils';
import { UploadButton } from '../../../../components/UploadButtonComponent';
import DeliveryUnitSaleCatalogDuplicateDialog from '../DeliveryUnitSaleCatalogDuplicateDialog';
import DeliveryUnitSaleCatalogEditDialog from '../DeliveryUnitSaleCatalogEditDialog';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';

interface IProps {
  party: GQLParty;
}

export const DeliveryUnitSaleCatalogDetails: React.FC<IProps> = ({ party }: IProps) => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const { t: translate } = useTranslation();
  const {
    deliveryUnitId,
    deliveryUnitSaleCatalogId
  } = ProducerNavigation.deliveryUnitSaleCatalogDetail.paramsFromPath(useRouteMatch());
  const [tabValue, setTabValue] = useState('EUR');
  const history = useHistory();

  const handleTabChange = (event: any, newValue: string) => {
    setTabValue(newValue);
  };

  const [
    publishSaleCatalog,
    {}
  ] = GQLOperationBuilder.deliveryUnitSaleCatalog.publishDeliveryUnitSaleCatalog();

  const [deleteSaleCatalog, {}] = GQLOperationBuilder.deliveryUnitSaleCatalog.deleteDeliveryUnitSaleCatalog();

  const [
    createPlatformProducerContractChangeRequest,
    {}
  ] = GQLOperationBuilder.request.createPlatformProducerContractChangeRequest();

  const [
    importCompleteInventoryIntoCatalog,
    {}
  ] = GQLOperationBuilder.deliveryUnitSaleCatalog.importDeliveryUnitCurrentCompleteInventoryIntoCatalog();

  const [
    exportDeliveryUnitAccessoryPricingCsvFromCatalog,
    {}
  ] = GQLOperationBuilder.deliveryUnitSaleCatalog.exportDeliveryUnitAccessoryPricingCsvFromCatalogLazy({
    onCompleted: result => {
      const bytes = CommonLogic.Base64.fromBase64(OperationsHelper.retrieveBytesFromGQLResponse(result));
      saveCsvFile(bytes, 'delivery_unit_sale_catalog_accessory_pricing.csv');
      successNotification(
        toast,
        'Delivery unit sale catalog accessories pricing downloaded successfully',
        () => {}
      );
    }
  });
  const [
    exportDeliveryUnitPiecePricingCsvFromCatalog,
    {}
  ] = GQLOperationBuilder.deliveryUnitSaleCatalog.exportDeliveryUnitPiecePricingCsvFromCatalogLazy({
    onCompleted: result => {
      const bytes = CommonLogic.Base64.fromBase64(OperationsHelper.retrieveBytesFromGQLResponse(result));
      saveCsvFile(bytes, 'delivery_unit_sale_catalog_piece_pricing.csv');
      successNotification(
        toast,
        'Delivery unit sale catalog pieces pricing downloaded successfully',
        () => {}
      );
    }
  });

  const [
    importPiecePricingCsvIntoCatalog,
    {}
  ] = GQLOperationBuilder.deliveryUnitSaleCatalog.importDeliveryUnitPiecePricingCsvIntoCatalog({
    onCompleted: async () => {
      successNotification(toast, 'Delivery unit sale catalog pieces pricing imported successfully', () => {});
      await reloadData();
    }
  });

  const [
    importAccessoryPricingCsvIntoCatalog,
    {}
  ] = GQLOperationBuilder.deliveryUnitSaleCatalog.importDeliveryUnitAccessoryPricingCsvIntoCatalog({
    onCompleted: async () => {
      successNotification(
        toast,
        'Delivery unit sale catalog accessories pricing imported successfully',
        () => {}
      );
      await reloadData();
    }
  });

  const mapResponseToCurrentSaleCatalog = (
    response: any
  ): GQLDeliveryUnitSaleCatalogWithPlatformProducerContract => {
    const saleCatalog = GQLOperationBuilder.deliveryUnitSaleCatalog
      .deliveryUnitSaleCatalogWithPlatformProducerContractArrayFromResponse(response)
      .find(c => c.deliveryUnitSaleCatalog.id === deliveryUnitSaleCatalogId);
    if (saleCatalog === undefined)
      throw new Exception(`Cannot find sale catalog ${deliveryUnitSaleCatalogId} in list of sale catalogs`);
    return saleCatalog;
  };

  const {
    data: saleCatalogsData,
    loading: saleCatalogsLoading,
    error: saleCatalogsError,
    refetch: refetchSaleCatalogs
  } = GQLOperationBuilder.deliveryUnitSaleCatalog.getDeliveryUnitSaleCatalogs(
    {
      producerPartyId: party.id,
      deliveryUnitId: deliveryUnitId
    },
    { fetchPolicy: 'network-only' }
  );

  const reloadData = async () => {
    return refetchSaleCatalogs();
  };

  if (saleCatalogsLoading) return <LoadingSpinner />;

  if (saleCatalogsError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={saleCatalogsError?.stack}
        callback={refetchSaleCatalogs}
        message={saleCatalogsError.message}
      />
    );

  const piecesInventoryColumns = [
    'Piece Name',
    'Size Type',
    'Material Type',
    'Plating Type',
    'Inventory Reference'
  ];

  const accessoriesInventoryColumns = [
    'Accessory Name',
    'Size Type',
    'Material Type',
    'Plating Type',
    'Inventory Reference'
  ];

  const getPieceInventoryRows = () => {
    return mapResponseToCurrentSaleCatalog(
      saleCatalogsData
    ).deliveryUnitSaleCatalog.inventoryCatalog.pieceInventorys.map(pieceInventory => {
      return {
        id: pieceInventory.id,
        cells: [
          pieceInventory.pieceTitle.en,
          pieceInventory.pieceReference.pieceConfiguration.sizeType,
          pieceInventory.pieceReference.pieceConfiguration.materialType,
          pieceInventory.pieceReference.pieceConfiguration.platingType,
          pieceInventory.inventoryReference
        ]
      };
    });
  };

  const getAccessoriesInventoryRows = () => {
    return mapResponseToCurrentSaleCatalog(
      saleCatalogsData
    ).deliveryUnitSaleCatalog.inventoryCatalog.accessoryInventorys.map(accessoryInventory => {
      return {
        id: accessoryInventory.id,
        cells: [
          accessoryInventory.accessoryTitle.en,
          accessoryInventory.accessoryReference.accessoryConfiguration.sizeType,
          accessoryInventory.accessoryReference.accessoryConfiguration.materialType,
          accessoryInventory.accessoryReference.accessoryConfiguration.platingType,
          accessoryInventory.inventoryReference
        ]
      };
    });
  };

  return (
    <Fragment>
      <Grid item style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex' }}>
          <Typography style={{ marginRight: '2px' }} variant="h4">
            {mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.name.en}
          </Typography>
          {!mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished && (
            <EditIcon
              fontSize="small"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowEditDialog(true)}
            />
          )}
        </div>
        <SectionCard title="Overview">
          <ListItemText
            primary={'Name'}
            secondary={mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.name.en}
          />
          <ListItemText
            primary={'Description'}
            secondary={
              mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.description.en
            }
          />
          <ListItemText
            primary={'Delivery Unit Sale Catalog Published'}
            secondary={
              <Chip
                size="small"
                mr={1}
                mb={1}
                label={
                  mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished
                    ? 'YES'
                    : 'NO'
                }
                $active={mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished}
              />
            }
          />
          <ListItemText
            primary={'Platform Producer Contract Active'}
            secondary={
              <Chip
                size="small"
                mr={1}
                mb={1}
                label={
                  mapResponseToCurrentSaleCatalog(saleCatalogsData).platformProducerContractActive
                    ? 'YES'
                    : 'NO'
                }
                $active={mapResponseToCurrentSaleCatalog(saleCatalogsData).platformProducerContractActive}
              />
            }
          />
          <ListItemText
            primary={'Updated At'}
            secondary={new Date(
              Date.parse(mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.updatedAt)
            ).toLocaleString()}
          />
        </SectionCard>
        <div style={{ display: 'flex' }}>
          {!mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished && (
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() =>
                publishSaleCatalog({
                  variables: {
                    deliveryUnitSaleCatalogId: mapResponseToCurrentSaleCatalog(saleCatalogsData)
                      .deliveryUnitSaleCatalog.id
                  }
                }).then(_result => {
                  reloadData();
                  successNotification(toast, 'Delivery unit sale catalog published successfully', () => {});
                })
              }
              disabled={mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished}
            >
              Publish
            </Button>
          )}
          {mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished &&
            !mapResponseToCurrentSaleCatalog(saleCatalogsData).platformProducerContractActive && (
              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() =>
                  createPlatformProducerContractChangeRequest({
                    variables: {
                      deliveryUnitId: mapResponseToCurrentSaleCatalog(saleCatalogsData)
                        .deliveryUnitSaleCatalog.deliveryUnitId,
                      deliveryUnitSaleCatalogId: mapResponseToCurrentSaleCatalog(saleCatalogsData)
                        .deliveryUnitSaleCatalog.id
                    }
                  }).then(_result => {
                    history.push(
                      ProducerNavigation.deliveryUnitSaleCatalogList.pathFromParams({
                        deliveryUnitId: deliveryUnitId
                      })
                    );
                  })
                }
                style={{ marginLeft: '8px' }}
                disabled={mapResponseToCurrentSaleCatalog(saleCatalogsData).platformProducerContractActive}
              >
                Promote to active contract
              </Button>
            )}
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={() => {
              setShowDuplicateDialog(true);
            }}
            style={{ marginLeft: '4px' }}
          >
            Duplicate
          </Button>
          {!mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished && (
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() =>
                deleteSaleCatalog({
                  variables: {
                    deliveryUnitSaleCatalogId: mapResponseToCurrentSaleCatalog(saleCatalogsData)
                      .deliveryUnitSaleCatalog.id
                  }
                }).then(_result => {
                  successNotification(toast, 'Delivery unit sale catalog deleted successfully', () => {});
                  history.push(
                    ProducerNavigation.deliveryUnitSaleCatalogList.pathFromParams({
                      deliveryUnitId: deliveryUnitId
                    })
                  );
                })
              }
              disabled={mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished}
              style={{ marginLeft: '4px' }}
            >
              Delete
            </Button>
          )}
        </div>
      </Grid>
      {mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.pricingCatalogs.length >
        0 && (
        <Grid item style={{ marginTop: '8px' }}>
          <SectionCard title="Selling prices">
            <TabContext value={tabValue}>
              <TabList
                onChange={(event, tabIndex) => handleTabChange(event, tabIndex)}
                aria-label="currency-tabs"
              >
                {mapResponseToCurrentSaleCatalog(
                  saleCatalogsData
                ).deliveryUnitSaleCatalog.pricingCatalogs.map(pricingCatalog => {
                  return (
                    <Tab
                      label={`${pricingCatalog.currency} Pricing Catalog`}
                      value={pricingCatalog.currency}
                    />
                  );
                })}
              </TabList>
              {mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.pricingCatalogs.map(
                pricingCatalog => {
                  const piecePricingColumns = [
                    'Size Type',
                    'Material Type',
                    'Plating Type',
                    'Stones Placement Type',
                    'Price'
                  ];

                  const accessoriesPricingColumns = ['Size Type', 'Material Type', 'Plating Type', 'Price'];

                  const getPiecePricingRows = () => {
                    return pricingCatalog.pieces.map(piecePrice => {
                      return {
                        id: piecePrice.pieceReference.pieceId,
                        cells: [
                          piecePrice.pieceReference.pieceConfiguration.sizeType,
                          piecePrice.pieceReference.pieceConfiguration.materialType,
                          piecePrice.pieceReference.pieceConfiguration.platingType,
                          piecePrice.pieceReference.pieceConfiguration.stonesPlacementType,
                          <PriceLabel price={piecePrice.price} />
                        ]
                      };
                    });
                  };

                  const getAccessoryPricingRows = () => {
                    return pricingCatalog.accessories.map(accessoryPrice => {
                      return {
                        id: accessoryPrice.accessoryReference.accessoryId,
                        cells: [
                          accessoryPrice.accessoryReference.accessoryConfiguration.sizeType,
                          accessoryPrice.accessoryReference.accessoryConfiguration.materialType,
                          accessoryPrice.accessoryReference.accessoryConfiguration.platingType,
                          <PriceLabel price={accessoryPrice.price} />
                        ]
                      };
                    });
                  };

                  return (
                    <TabPanel value={pricingCatalog.currency}>
                      <JwlTable
                        title="Pieces Pricing Catalog"
                        description="If you have been reading the overrides documentation page but you are, add more metadata..."
                        columns={piecePricingColumns}
                        rows={getPiecePricingRows()}
                        height="400px"
                        size="small"
                        children={
                          <div style={{ display: 'flex' }}>
                            {!mapResponseToCurrentSaleCatalog(saleCatalogsData)
                              .deliveryUnitSaleCatalogPublished && (
                              <UploadButton
                                id={`piecesPricingCatalog_${
                                  mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.id
                                }`}
                                importButtonLabel={'Import'}
                                onFileSelected={(piecePricingCsv: File) => {
                                  importPiecePricingCsvIntoCatalog({
                                    variables: {
                                      deliveryUnitSaleCatalogId: deliveryUnitSaleCatalogId,
                                      csv: piecePricingCsv
                                    }
                                  });
                                }}
                              />
                            )}
                            <Button
                              variant="contained"
                              color={'primary'}
                              onClick={() => {
                                exportDeliveryUnitPiecePricingCsvFromCatalog({
                                  variables: {
                                    deliveryUnitSaleCatalogId: deliveryUnitSaleCatalogId,
                                    currency: pricingCatalog.currency
                                  }
                                });
                              }}
                              style={{ marginLeft: '4px' }}
                            >
                              <LogOut />
                              <span style={{ marginLeft: '4px' }}>Export</span>
                            </Button>
                          </div>
                        }
                      />
                      <StyledDivider />
                      <JwlTable
                        title="Accessories Pricing Catalog"
                        description="If you have been reading the overrides documentation page but you are, add more metadata..."
                        columns={accessoriesPricingColumns}
                        rows={getAccessoryPricingRows()}
                        height="400px"
                        size="small"
                        children={
                          <div style={{ display: 'flex' }}>
                            {!mapResponseToCurrentSaleCatalog(saleCatalogsData)
                              .deliveryUnitSaleCatalogPublished && (
                              <UploadButton
                                id={`accessoriesPricingCatalog_${
                                  mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog.id
                                }`}
                                importButtonLabel={'Import'}
                                onFileSelected={(accessoryPricingCsv: File) => {
                                  importAccessoryPricingCsvIntoCatalog({
                                    variables: {
                                      deliveryUnitSaleCatalogId: deliveryUnitSaleCatalogId,
                                      csv: accessoryPricingCsv
                                    }
                                  });
                                }}
                              />
                            )}
                            <Button
                              variant="contained"
                              color={'primary'}
                              onClick={() => {
                                exportDeliveryUnitAccessoryPricingCsvFromCatalog({
                                  variables: {
                                    deliveryUnitSaleCatalogId: deliveryUnitSaleCatalogId,
                                    currency: pricingCatalog.currency
                                  }
                                });
                              }}
                              style={{ marginLeft: '4px' }}
                            >
                              <LogOut />
                              <span style={{ marginLeft: '4px' }}>Export</span>
                            </Button>
                          </div>
                        }
                      />
                    </TabPanel>
                  );
                }
              )}
            </TabContext>
          </SectionCard>
        </Grid>
      )}
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Asset inventory">
          <JwlTable
            title="Pieces Inventory"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={piecesInventoryColumns}
            rows={getPieceInventoryRows()}
            height="400px"
            size="small"
          />
          <StyledDivider />
          <JwlTable
            title="Accessories Inventory"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={accessoriesInventoryColumns}
            rows={getAccessoriesInventoryRows()}
            height="400px"
            size="small"
          />
          {!mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalogPublished && (
            <Button
              variant="contained"
              color={'primary'}
              onClick={() =>
                importCompleteInventoryIntoCatalog({
                  variables: {
                    deliveryUnitSaleCatalogId: mapResponseToCurrentSaleCatalog(saleCatalogsData)
                      .deliveryUnitSaleCatalog.id
                  }
                }).then(_result => {
                  reloadData();
                  successNotification(
                    toast,
                    'Added all current pieces & accessories inventory items to delivery unit sale catalog.',
                    () => {}
                  );
                })
              }
              style={{ marginTop: '8px' }}
            >
              <LogIn />
              <span style={{ marginLeft: '4px' }}>Import all current inventories</span>
            </Button>
          )}
        </SectionCard>
      </Grid>
      {showEditDialog && (
        <DeliveryUnitSaleCatalogEditDialog
          party={party}
          deliveryUnitSaleCatalog={mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog}
          onClose={success => {
            setShowEditDialog(false);
            if (success) {
              successNotification(toast, 'Delivery unit sale catalog updated successfully', () => {});
              reloadData();
            }
          }}
        ></DeliveryUnitSaleCatalogEditDialog>
      )}
      {showDuplicateDialog && (
        <DeliveryUnitSaleCatalogDuplicateDialog
          party={party}
          sourceDeliveryUnitSaleCatalog={
            mapResponseToCurrentSaleCatalog(saleCatalogsData).deliveryUnitSaleCatalog
          }
          onClose={(success, _newDeliveryUnitSaleCatalogId) => {
            setShowDuplicateDialog(false);
            if (success) {
              successNotification(toast, 'New delivery unit sale catalog created successfully', () => {});
              history.push(
                ProducerNavigation.deliveryUnitSaleCatalogList.pathFromParams({
                  deliveryUnitId: deliveryUnitId
                })
              );
            }
          }}
        ></DeliveryUnitSaleCatalogDuplicateDialog>
      )}
    </Fragment>
  );
};
